import React from 'react';

const PasswordStrengthIndicator = ({ password }) => {
  const calculateStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    // Calculate strength based on criteria
    if (checks.length) strength += 1;
    if (checks.lowercase) strength += 1;
    if (checks.uppercase) strength += 1;
    if (checks.number) strength += 1;
    if (checks.special) strength += 1;

    // Bonus for longer passwords
    if (password.length >= 12) strength += 1;
    if (password.length >= 16) strength += 1;

    // Cap at 7 for very strong passwords
    strength = Math.min(strength, 7);

    if (strength <= 2) {
      return { strength, label: 'Weak', color: 'red', percentage: 33 };
    } else if (strength <= 4) {
      return { strength, label: 'Fair', color: 'orange', percentage: 50 };
    } else if (strength <= 5) {
      return { strength, label: 'Good', color: 'yellow', percentage: 66 };
    } else {
      return { strength, label: 'Strong', color: 'green', percentage: 100 };
    }
  };

  const { strength, label, color, percentage } = calculateStrength(password);

  const getColorClasses = (color) => {
    switch (color) {
      case 'red':
        return {
          bg: 'bg-red-500',
          text: 'text-red-600',
          border: 'border-red-500',
        };
      case 'orange':
        return {
          bg: 'bg-orange-500',
          text: 'text-orange-600',
          border: 'border-orange-500',
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-500',
          text: 'text-yellow-600',
          border: 'border-yellow-500',
        };
      case 'green':
        return {
          bg: 'bg-green-500',
          text: 'text-green-600',
          border: 'border-green-500',
        };
      default:
        return {
          bg: 'bg-gray-300',
          text: 'text-gray-600',
          border: 'border-gray-300',
        };
    }
  };

  const colorClasses = getColorClasses(color);

  const getRequirements = (password) => {
    return [
      { met: password.length >= 8, text: 'At least 8 characters' },
      { met: /[a-z]/.test(password), text: 'One lowercase letter' },
      { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
      { met: /[0-9]/.test(password), text: 'One number' },
      { met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), text: 'One special character' },
    ];
  };

  const requirements = getRequirements(password);

  if (!password) return null;

  return (
    <div className="mt-2">
      {/* Strength Bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-semibold ${colorClasses.text}`}>
            Password Strength: {label}
          </span>
          <span className={`text-xs ${colorClasses.text}`}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-2 transition-all duration-300 ${colorClasses.bg}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Requirements List */}
      <div className="mt-3 space-y-1">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center text-xs">
            <span className={`mr-2 ${req.met ? 'text-green-500' : 'text-gray-400'}`}>
              {req.met ? '✓' : '○'}
            </span>
            <span className={req.met ? 'text-gray-600' : 'text-gray-400'}>
              {req.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;




